import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import {Stack, Typography, Breadcrumbs, Button, MenuItem, alpha, Menu} from '@mui/material'
import {useRef, useState} from 'react'

import {ExpertSystemUsageLocationHistory} from '../utils'

export const ExpertSystemUsageListBreadcrumbs = ({
  locationHistory,
  goToLocation,
  title,
  locationSiblings
}: {
  locationHistory: ExpertSystemUsageLocationHistory[]
  goToLocation: (id: string) => void
  title: string
  locationSiblings: ExpertSystemUsageLocationHistory[]
}) => {
  const [openDropdown, setOpenDropdown] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const hasLocationHistory = locationHistory.length > 0
  const hasDropdown = locationSiblings.length > 0

  const handleToggle = () => {
    if (!hasDropdown) return
    setOpenDropdown((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpenDropdown(false)
  }

  const handleMenuItemOnClick = (id: string) => {
    setOpenDropdown(false)
    goToLocation(id)
  }

  return (
    <Stack sx={{width: '100%'}}>
      <Stack direction="row" alignItems="flex-start" mt={1}>
        <Breadcrumbs
          separator={<b>|</b>}
          sx={{textDecoration: 'none', color: ({palette}) => alpha(palette.common.white, 0.6)}}
        >
          {hasLocationHistory && (
            <Typography
              data-test-id={`expert-system-usage-breadcrumb-item-${title}`}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer'
              }}
              variant="h5"
              onClick={() => goToLocation('')}
            >
              {title}
            </Typography>
          )}
          {locationHistory.map((breadcrumb, index) => {
            if (index === locationHistory.length - 1) {
              return null
            }
            return (
              <Typography
                data-test-id={`expert-system-usage-breadcrumb-item-${breadcrumb.name}`}
                onClick={() => goToLocation(breadcrumb.id)}
                key={breadcrumb.id}
                component="span"
                color="inherit"
                sx={{
                  cursor: 'pointer'
                }}
              >
                {breadcrumb.name}
              </Typography>
            )
          })}
        </Breadcrumbs>
      </Stack>
      <Stack direction="row" alignItems="baseline">
        <Button
          ref={anchorRef}
          id="expert-system-usage-breadcrumb-button"
          variant="text"
          onClick={handleToggle}
          sx={{
            mt: 1,
            p: 0,
            color: 'primary.contrastText',
            width: '100%',
            justifyContent: 'flex-start'
          }}
          endIcon={
            !hasDropdown ? null : openDropdown ? (
              <ArrowDropUpIcon data-test-id={'expert-system-usage-arrow-drop-up-icon-btn'} />
            ) : (
              <ArrowDropDownIcon data-test-id={'expert-system-usage-arrow-drop-down-icon-btn'} />
            )
          }
        >
          <Typography variant="h2" fontWeight="bold" color="inherit">
            {locationHistory.length ? locationHistory[locationHistory.length - 1]?.name : title}
          </Typography>
        </Button>
        {hasDropdown && (
          <Menu
            anchorEl={anchorRef.current}
            open={openDropdown}
            onClose={handleClose}
            sx={{width: '100%', mt: 1}}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            MenuListProps={{
              sx: {
                width: anchorRef.current?.offsetWidth || '100%'
              }
            }}
          >
            {locationSiblings.map((location) => {
              if (location.name === locationHistory[locationHistory.length - 1]?.name) return null
              return (
                <MenuItem
                  key={location.id}
                  onClick={() => {
                    handleMenuItemOnClick(location.id)
                  }}
                  data-test-id={`expert-system-usage-menu-item-${location.name}`}
                >
                  {location.name}
                </MenuItem>
              )
            })}
          </Menu>
        )}
      </Stack>
    </Stack>
  )
}
